import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  LockOutlined as LockIcon,
} from "@mui/icons-material";
import { supabase } from "../lib/supabase";

interface Props {
  open: boolean;
  invitedBy: string | null;
  projectTitle: string | null;
  defaultName: string | null;
  defaultEmail: string | null;
  onComplete: () => void;
}

export function SetPasswordModal({
  open,
  invitedBy,
  projectTitle,
  defaultName,
  defaultEmail,
  onComplete,
}: Props) {
  const [name, setName] = useState(defaultName ?? "");
  const email = defaultEmail ?? "";

  // Sync name when props arrive asynchronously (AuthCallback sets them after mount)
  useEffect(() => {
    if (open) {
      setName(defaultName ?? "");
    }
  }, [defaultName, open]);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setError(null);

    if (!name.trim()) {
      setError("Please enter your name.");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }

    setSaving(true);

    const { error: updateError } = await supabase.auth.updateUser({
      password,
      data: { full_name: name.trim(), name: name.trim() },
    });

    if (updateError) {
      setError(updateError.message);
      setSaving(false);
      return;
    }

    // Update the users table with name (email is already set from invite)
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      await supabase
        .from("users")
        .update({ name: name.trim() })
        .eq("id", user.id);
    }

    setSaving(false);
    onComplete();
  };

  return (
    <Dialog open={open} maxWidth="xs" fullWidth disableEscapeKeyDown>
      <DialogContent sx={{ p: 4 }}>
        <Stack spacing={3}>
          {/* Header */}
          <Box sx={{ textAlign: "center" }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                bgcolor: "primary.light",
                color: "primary.contrastText",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 2,
              }}
            >
              <LockIcon />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Welcome to BuildBridge!
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {invitedBy
                ? `${invitedBy} has added you to ${projectTitle ? `"${projectTitle}"` : "a project"}.`
                : "You've been invited to a project."}
              {" "}Complete your profile to get started.
            </Typography>
          </Box>

          <Divider />

          {error && <Alert severity="error">{error}</Alert>}

          {/* Email — read-only, shown for confirmation */}
          <TextField
            label="Email"
            type="email"
            fullWidth
            value={email}
            disabled
            helperText="This is the email your account is registered to."
          />

          <TextField
            label="Your full name"
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            disabled={saving}
            placeholder="e.g. Sarah Johnson"
          />

          <TextField
            label="Password"
            type={showPassword ? "text" : "password"}
            fullWidth
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={saving}
            helperText="At least 8 characters"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword((prev) => !prev)}
                    edge="end"
                    size="small"
                  >
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            label="Confirm password"
            type={showConfirm ? "text" : "password"}
            fullWidth
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            disabled={saving}
            onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirm((prev) => !prev)}
                    edge="end"
                    size="small"
                  >
                    {showConfirm ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={handleSubmit}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : null}
          >
            {saving ? "Setting up account…" : "Set password & get started"}
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
